import React, { useRef } from 'react';

import axios from 'axios';
import { getTranslate } from 'react-localize-redux';

import { useCountdown } from '../../../../utils/hooks';
import { apiServices } from '../../../../weborder/services';
import { Button } from '../../../atoms';

interface RequestSmsCodeCallParams {
  PhoneNumber: string;
  Fallback: string;
}

type Props = {
  onSubmit?(): void;
  onSuccess?(): void;
  onError?(error: Error): void;
  phoneNumber: string;
  translate: ReturnType<typeof getTranslate>;
};

export function PhoneCallButton(props: Props): JSX.Element {
  const { onError, onSubmit, onSuccess, phoneNumber, translate } = props;

  const isCallRequested = useRef(false);
  const isSubmitting = useRef(false);
  const [countdown, startCountdownAgain] = useCountdown(30);

  const requestSmsCodeCall = async (params: RequestSmsCodeCallParams) => {
    try {
      await axios.post('/Account/RequestPhoneLoginCodeSms/', params);
    } catch (error) {
      apiServices.throwIfServerError(error);
    }
  };

  const handleRequestPhoneCall = async () => {
    try {
      isSubmitting.current = true;
      isCallRequested.current = true;

      if (onSubmit) {
        onSubmit();
      }

      await requestSmsCodeCall({
        PhoneNumber: phoneNumber,
        Fallback: 'PhoneCall',
      });

      startCountdownAgain();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      if (onError) {
        onError(error);
      }
    } finally {
      isCallRequested.current = false;
    }
  };

  const getRequestPhoneCallButtonText = (): string => {
    const callStatusMessage = isCallRequested.current
      ? 'LoginConfirm_LoginConfirmDialog_RequestPhonecallAgain'
      : 'LoginConfirm_LoginConfirmDialog_RequestPhonecall';

    if (countdown > 0) {
      return `${translate(callStatusMessage)} ${countdown}s`;
    }

    return `${translate('Request_code_via_phone_call')}`;
  };

  return (
    <Button
      data-fd="request-call"
      disabled={countdown > 0 || isSubmitting.current}
      onClick={handleRequestPhoneCall}
      size="small"
      variant="secondary"
    >
      {getRequestPhoneCallButtonText()}
    </Button>
  );
}
